import React from 'react';
import type { InputFixtureState, InputsFixtureState } from 'react-cosmos-core';
import { isEqual } from 'react-cosmos-core';
import { IconButton32 } from '../../components/buttons/index.js';
import { RotateCcwIcon } from '../../components/icons/index.js';
import {
  SidePanelActions,
  SidePanelBody,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelTitle,
} from '../../components/SidePanel.js';
import { InputsActionSlot } from '../../slots/InputsActionSlot.js';
import { InputSlot } from '../../slots/InputSlot.js';
import type { SetInputsFixtureState } from './shared.js';

type Props = {
  fixtureState: InputsFixtureState | undefined;
  actionOrder: string[];
  onFixtureStateChange: SetInputsFixtureState;
};

export function InputsPanel({
  fixtureState,
  actionOrder,
  onFixtureStateChange,
}: Props) {
  const handleInputsReset = React.useCallback(
    () => onFixtureStateChange(resetInputs),
    [onFixtureStateChange]
  );

  const inputs = fixtureState ?? {};
  if (Object.keys(inputs).length === 0) return null;

  return (
    <SidePanelContainer>
      <SidePanelHeader>
        <SidePanelTitle label="Inputs" />
        <SidePanelActions>
          <IconButton32
            title="Reset to default values"
            icon={<RotateCcwIcon />}
            disabled={areInputsUnchanged(inputs)}
            onClick={handleInputsReset}
          />
          <InputsActionSlot slotProps={{ inputs }} plugOrder={actionOrder} />
        </SidePanelActions>
      </SidePanelHeader>
      <SidePanelBody>
        {Object.keys(inputs).map(inputName => (
          <InputSlot
            key={inputName}
            slotProps={{
              inputName,
              input: inputs[inputName],
              onFixtureStateChange,
            }}
          />
        ))}
      </SidePanelBody>
    </SidePanelContainer>
  );
}

function areInputsUnchanged(inputs: InputsFixtureState) {
  return Object.keys(inputs).every(inputName =>
    isEqual(inputs[inputName].currentValue, inputs[inputName].defaultValue)
  );
}

function resetInputs(fixtureState: InputsFixtureState | undefined) {
  const inputs = fixtureState ? { ...fixtureState } : {};
  Object.keys(inputs).forEach(inputName => {
    inputs[inputName] = resetInput(inputs[inputName]);
  });
  return inputs;
}

function resetInput<TInput extends InputFixtureState>(input: TInput) {
  return { ...input, currentValue: input.defaultValue };
}
